import { createSlice, type PayloadAction } from '@reduxjs/toolkit';
import type { CustomerInfo } from '../types/customerInfo';

export interface BillingForm {
  email: string;
  firstName: string;
  lastName: string;
  country: string;
  address: string;
  city: string;
  zip: string;
  vat: string;
  company: string;
}

export interface BillingState {
  form: BillingForm;
  info: Partial<CustomerInfo>;
  hasBillingInfo: boolean;
  defaultPaymentMethod: string | null;
  errors: Record<string, string | null>;
  countryState: string;
}

const initialState: BillingState = {
  form: {
    email: '',
    firstName: '',
    lastName: '',
    country: '',
    address: '',
    city: '',
    zip: '',
    vat: '',
    company: '',
  },
  info: {},
  hasBillingInfo: false,
  defaultPaymentMethod: null,
  errors: {},
  countryState: '',
};

const billingSlice = createSlice({
  name: 'billing',
  initialState,
  reducers: {
    setForm(state, action: PayloadAction<Partial<BillingForm>>) {
      state.form = { ...state.form, ...action.payload };
    },
    setError(state, action: PayloadAction<Record<string, string>>) {
      state.errors = { ...state.errors, ...action.payload };
    },
    clearError(state, action: PayloadAction<string>) {
      state.errors[action.payload] = null;
    },
    setCountryState(state, action: PayloadAction<string>) {
      state.countryState = action.payload;
    },
    setInfo(state, action: PayloadAction<Partial<CustomerInfo>>) {
      state.info = { ...state.info, ...action.payload };
    },
    setHasBillingInfo(state, action: PayloadAction<boolean>) {
      state.hasBillingInfo = action.payload;
    },
    setAddress(state, action: PayloadAction<Partial<BillingForm>>) {
      state.form = { ...state.form, ...action.payload };
    },
    setZip(state, action: PayloadAction<Partial<BillingForm>>) {
      state.form = { ...state.form, ...action.payload };
    },
    setDefaultPaymentMethod(state, action: PayloadAction<string | null>) {
      state.defaultPaymentMethod = action.payload;
    },
    clearForm(state) {
      state.form = initialState.form;
      state.errors = initialState.errors;
      state.countryState = initialState.countryState;
    },
    clearInfo(state) {
      state.info = initialState.info;
      state.hasBillingInfo = initialState.hasBillingInfo;
      state.defaultPaymentMethod = initialState.defaultPaymentMethod;
    },
  },
});

export const {
  setForm,
  setError,
  clearError,
  setCountryState,
  setInfo,
  setHasBillingInfo,
  setAddress,
  setZip,
  setDefaultPaymentMethod,
  clearForm,
  clearInfo,
} = billingSlice.actions;

export default billingSlice.reducer;
